import { getMedia } from '../db';
import { renderMixedAudioWav } from '../audioMix';
import { getTimelineCaptionCues } from '../captionTiming';
import { computeTotalDuration } from '../timeline';
import { withEffectiveSettings } from '../effectiveSettings';
import { runFfmpegExport, RenderCancelledError } from './runFfmpegExport';
import type { ReelProject, SharedDefaults } from '@/types';

const DEFAULT_WATERMARK_URL = '/assets/watermark-default.svg';

export interface ExportProgress {
  stage: 'preparing' | 'mixing-audio' | 'rendering-frames' | 'encoding' | 'done';
  progress: number; // 0..100
}

export interface ExportOptions {
  onProgress?: (p: ExportProgress) => void;
  onLog?: (message: string) => void;
}

export interface ExportHandle {
  promise: Promise<Blob>;
  cancel: () => void;
}

/**
 * Renders a full MP4 for one project: resolves effective settings, loads and
 * decodes all referenced media, mixes the final audio track, then draws every
 * frame and hands them to FFmpeg (run directly here on the main thread — see
 * runFfmpegExport.ts for why it's not wrapped in a custom Worker).
 */
export async function exportProject(
  project: ReelProject,
  shared: SharedDefaults,
  options: ExportOptions = {}
): Promise<ExportHandle> {
  const effectiveProject = withEffectiveSettings(project, shared);
  options.onProgress?.({ stage: 'preparing', progress: 2 });

  if (effectiveProject.images.length === 0) {
    throw new ExportError('This Reel has no images yet — add at least one image before exporting.');
  }

  const totalDuration = computeTotalDuration(effectiveProject);
  if (totalDuration <= 0) {
    throw new ExportError('This Reel has no duration — check image durations and voiceover timing.');
  }

  // --- Decode images -------------------------------------------------------
  const uniqueImageIds = Array.from(new Set(effectiveProject.images.map((c) => c.mediaId)));
  const images = await Promise.all(
    uniqueImageIds.map(async (mediaId) => {
      const record = await getMedia(mediaId);
      if (!record) throw new ExportError(`A referenced image could not be found in local storage (it may have been cleared).`);
      const bitmap = await createImageBitmap(record.blob);
      return { mediaId, bitmap };
    })
  );

  // --- Watermark -------------------------------------------------------------
  let watermarkBitmap: ImageBitmap | null = null;
  if (effectiveProject.watermark.enabled) {
    if (effectiveProject.watermark.source === 'custom' && effectiveProject.watermark.mediaId) {
      const record = await getMedia(effectiveProject.watermark.mediaId);
      if (record) watermarkBitmap = await createImageBitmap(record.blob);
    } else {
      const response = await fetch(DEFAULT_WATERMARK_URL);
      const blob = await response.blob();
      watermarkBitmap = await createImageBitmap(blob);
    }
  }

  // --- Audio mixing ----------------------------------------------------------
  options.onProgress?.({ stage: 'mixing-audio', progress: 8 });
  let voiceoverBlob: Blob | null = null;
  if (effectiveProject.voiceover.generatedMediaId) {
    const record = await getMedia(effectiveProject.voiceover.generatedMediaId);
    voiceoverBlob = record?.blob ?? null;
  }
  let musicBlob: Blob | null = null;
  if (effectiveProject.music.enabled && effectiveProject.music.mediaId) {
    const record = await getMedia(effectiveProject.music.mediaId);
    musicBlob = record?.blob ?? null;
  }

  let audioWav: ArrayBuffer | null = null;
  if (voiceoverBlob || musicBlob) {
    const mixed = await renderMixedAudioWav({
      totalDurationSeconds: totalDuration,
      voiceover: effectiveProject.voiceover,
      voiceoverBlob,
      music: effectiveProject.music,
      musicBlob,
    });
    audioWav = await mixed.arrayBuffer();
  }

  const captionCues = getTimelineCaptionCues(effectiveProject);

  options.onProgress?.({ stage: 'rendering-frames', progress: 15 });

  const cancelToken = { cancelled: false };
  const promise = runFfmpegExport({
    project: effectiveProject,
    captionCues,
    images,
    watermarkBitmap,
    audioWav,
    totalDurationSeconds: totalDuration,
    widthPx: effectiveProject.export.widthPx,
    heightPx: effectiveProject.export.heightPx,
    frameRate: effectiveProject.export.frameRate,
    quality: effectiveProject.export.quality,
    onProgress: (progress, stage) => options.onProgress?.({ stage: mapStage(stage), progress }),
    onLog: options.onLog,
    cancelToken,
  });

  return { promise, cancel: () => (cancelToken.cancelled = true) };
}

function mapStage(stage: string): ExportProgress['stage'] {
  if (stage === 'rendering-frames') return 'rendering-frames';
  if (stage === 'encoding') return 'encoding';
  if (stage === 'done') return 'done';
  return 'preparing';
}

export class ExportError extends Error {}
